// Единый контракт адаптивной раскладки.
//
// Вписывание художественного прямоугольника и мобильная композиция — независимые решения:
// первое выбирает края привязки, второе — размер поля и компактный ряд UI.

import { DESIGN_HEIGHT, DESIGN_WIDTH, type Viewport } from './scene';

export type Composition = 'desktop' | 'mobile';

export interface LayoutBounds {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

export interface GameLayout {
  readonly viewport: Viewport;
  readonly composition: Composition;
  readonly bounds: LayoutBounds;
  readonly bottom: {
    readonly scale: number;
    readonly centerY: number;
    readonly shadowTop: number;
    readonly buttonBottom: number;
  };
}

const MOBILE_MAX_ASPECT: number = 4 / 3;
const DESKTOP_BAR_BOTTOM: number = 110;
const MOBILE_BAR_BOTTOM_PIXELS: number = 34;
const SHADOW_ABOVE_BAR: number = 80;
const MOBILE_LABEL_SIZE: number = 22;
const MOBILE_MIN_LABEL_PIXELS: number = 12;
const MOBILE_MAX_BAR_SCALE: number = 2.6;

/** В мобильной композиции тач-цели и нижняя полоса получают отдельный масштаб. */
export function isMobileLayout(viewport: Viewport): boolean {
  return viewport.width / viewport.height < MOBILE_MAX_ASPECT;
}

/** Не даёт круглой кнопке стать меньше заданного физического радиуса. */
export function minTouchableScale(viewport: Viewport, radius: number, minimumRadius: number, baseScale: number = 1): number {
  return Math.max(baseScale, minimumRadius / (radius * viewport.scale));
}

/** Отступ от фактического края в физических пикселях, переведённый в координаты макета. */
export function viewportEdge(viewport: Viewport, pixels: number = 20): number {
  return pixels / viewport.scale;
}

/** Зазор круглых кнопок одного ряда в углу макета: таблица выплат, правила и полный экран. */
export const ROW_BUTTON_GAP: number = 18;

/** На маленьком canvas декоративный зазор в единицах макета иначе превращается в пару пикселей. */
const MIN_ROW_GAP_PIXELS: number = 16;

/** Зазор ряда кнопок с поправкой на мелкий canvas. */
export function rowButtonGap(layout: GameLayout): number {
  if (layout.composition !== 'mobile')
    return ROW_BUTTON_GAP;

  return Math.max(ROW_BUTTON_GAP, MIN_ROW_GAP_PIXELS / layout.viewport.scale);
}

/** Собирает все общие для сцены правила раскладки. */
export function gameLayout(viewport: Viewport): GameLayout {
  const composition: Composition = isMobileLayout(viewport) ? 'mobile' : 'desktop';
  const bounds: LayoutBounds = viewport.fit === 'width'
    ? { left: -viewport.width / 2, right: viewport.width / 2, top: -viewport.height / 2, bottom: viewport.height / 2 }
    : { left: -DESIGN_WIDTH / 2, right: DESIGN_WIDTH / 2, top: -DESIGN_HEIGHT / 2, bottom: DESIGN_HEIGHT / 2 };
  const scale: number = composition === 'mobile'
    ? Math.min(MOBILE_MAX_BAR_SCALE, Math.max(1.75, MOBILE_MIN_LABEL_PIXELS / (MOBILE_LABEL_SIZE * viewport.scale)))
    : 1;
  const centerY: number = composition === 'mobile'
    ? bounds.bottom - MOBILE_BAR_BOTTOM_PIXELS / viewport.scale
    : bounds.bottom - DESKTOP_BAR_BOTTOM;
  const shadowTop: number = centerY - SHADOW_ABOVE_BAR * scale;

  return {
    viewport,
    composition,
    bounds,
    bottom: {
      scale,
      centerY,
      shadowTop,
      // Круглые кнопки примыкают к подложке сверху: это общая граница, а не отдельная
      // высота ряда, поэтому они не «висят» при изменении высоты canvas.
      buttonBottom: composition === 'mobile' ? shadowTop : bounds.bottom - 60,
    },
  };
}
